import { EMPTY_TILE, tileIdOf, tilesetIndexOf } from "../document/packed-tile.ts";
import { COLLISION_CELLS_PER_TILE, type MapDocument, type TilesetRef } from "../document/types.ts";

/**
 * The ground under a foot point, as the prop brush's ground rules read it (ADR-123 decision 3 and its note of
 * 2026-10-09 for P1.6-T192). A paint kind is a terrain entry, the terrain and colour the terrain palette names
 * (`terrainEntryKey`), or a tileset, for ground painted as plain tiles; a zone kind is a zone layer of one world, an
 * area over the paint. All are names the document already holds, so no game's ground or zone type is named here.
 */

/**
 * A zone kind: the id of a zone layer and the id of the world it is in, since layer ids are unique only within a
 * world.
 */
export interface ZoneKind {
  readonly zone: string;
  readonly world: string;
}

/**
 * One kind of ground: a terrain entry, a tileset, one tile of a tileset (`tile` present, the local tile id, flips
 * ignored; a kind without `tile` is any tile of its tileset) or a zone.
 */
export type GroundKind = { readonly terrain: string; readonly colour: number } | { readonly tileset: string; readonly tile?: number } | ZoneKind;

/**
 * The tile id `groundRulesMeet` gives a tile no rule names: no real tile has it.
 */
const OTHER_TILE = -1;

/**
 * Whether a point is inside a zone kind, for `groundAllows`.
 */
export type ZoneTest = (kind: ZoneKind) => boolean;

/**
 * A ground rule, an entry's or a set's: `only` lands where the ground is one of the kinds, `never` keeps off them.
 */
export interface GroundRule {
  readonly mode: "only" | "never";
  readonly kinds: readonly GroundKind[];
}

/**
 * What the eye sees at a foot point: walking the map's layers from the top, the first painted there answers. A
 * terrain layer answers at the corner vertex nearest the point, when that vertex's colour is not 0, since a shore
 * cell holds corners of two colours and the foot point's quarter is the one the art shows under it. A tile layer
 * answers at the cell the feet stand in, the one holding the pixel row above the foot line, when it holds a tile,
 * with that tile's id. Other kinds of layer are passed over.
 *
 * @param map The map.
 * @param tilesets The world's tilesets, for a packed tile's tileset.
 * @param tileSize Tile size in pixels.
 * @param footX The foot point's x in whole pixels.
 * @param footY The foot point's y in whole pixels: the bottom edge of the frame.
 * @returns The paint there, or null where nothing is painted.
 */
export function groundAt(map: MapDocument, tilesets: readonly TilesetRef[], tileSize: number, footX: number, footY: number): GroundKind | null {
  const vx = Math.min(map.width, Math.max(0, Math.round(footX / tileSize)));
  const vy = Math.min(map.height, Math.max(0, Math.round(footY / tileSize)));
  const tx = Math.floor(footX / tileSize);
  const ty = Math.floor((footY - 1) / tileSize);
  const onMap = tx >= 0 && ty >= 0 && tx < map.width && ty < map.height;
  for (let i = map.layers.length - 1; i >= 0; i -= 1) {
    const layer = map.layers[i];
    if (layer?.kind === "terrain") {
      const colour = layer.corners[vy * (map.width + 1) + vx] ?? 0;
      if (colour !== 0) {
        return { terrain: layer.terrain, colour };
      }
    } else if (layer?.kind === "tile" && onMap) {
      const packed = layer.tiles[ty * map.width + tx] ?? EMPTY_TILE;
      const tileset = packed === EMPTY_TILE ? undefined : tilesets[tilesetIndexOf(packed)];
      if (tileset !== undefined) {
        return { tileset: tileset.id, tile: tileIdOf(packed) };
      }
    }
  }
  return null;
}

/**
 * Everything painted at a foot point, filled in place by `paintAt` so a stroke's candidates allocate nothing: the
 * terrain and colour of every terrain layer painted at the vertex, and the tileset and tile id of a tile layer when
 * it is the top-most painted layer (`tileset` is null and `tile` -1 when none is).
 */
export interface PaintStack {
  readonly terrains: string[];
  readonly colours: number[];
  count: number;
  tileset: string | null;
  tile: number;
}

/**
 * An empty paint stack, to be reused across candidates.
 *
 * @returns The stack.
 */
export function createPaintStack(): PaintStack {
  return { terrains: [], colours: [], count: 0, tileset: null, tile: -1 };
}

/**
 * Reads everything painted at a foot point into a reused stack (ADR-123's note of 2026-10-13, P1.6-T250). Every
 * terrain layer painted at the vertex nearest the point (`groundAt`'s vertex) adds its terrain and colour, whatever
 * lies above or below it: a terrain under a transparent-edged one is still painted there. A tile layer adds nothing
 * to the terrains; its tileset and tile id are recorded only when the top-most painted layer is a tile layer, at the cell the
 * feet stand in, which is `groundAt`'s answer for tiles. The stack is filled in place and its two arrays are only
 * grown, so a candidate allocates nothing.
 *
 * @param map The map.
 * @param tilesets The world's tilesets, for a packed tile's tileset.
 * @param tileSize Tile size in pixels.
 * @param footX The foot point's x in whole pixels.
 * @param footY The foot point's y in whole pixels: the bottom edge of the frame.
 * @param out The stack to fill.
 */
export function paintAt(map: MapDocument, tilesets: readonly TilesetRef[], tileSize: number, footX: number, footY: number, out: PaintStack): void {
  const vx = Math.min(map.width, Math.max(0, Math.round(footX / tileSize)));
  const vy = Math.min(map.height, Math.max(0, Math.round(footY / tileSize)));
  const tx = Math.floor(footX / tileSize);
  const ty = Math.floor((footY - 1) / tileSize);
  const onMap = tx >= 0 && ty >= 0 && tx < map.width && ty < map.height;
  out.count = 0;
  out.tileset = null;
  out.tile = -1;
  let anyAbove = false;
  for (let i = map.layers.length - 1; i >= 0; i -= 1) {
    const layer = map.layers[i];
    if (layer?.kind === "terrain") {
      const colour = layer.corners[vy * (map.width + 1) + vx] ?? 0;
      if (colour !== 0) {
        out.terrains[out.count] = layer.terrain;
        out.colours[out.count] = colour;
        out.count += 1;
        anyAbove = true;
      }
    } else if (layer?.kind === "tile" && onMap && !anyAbove) {
      const packed = layer.tiles[ty * map.width + tx] ?? EMPTY_TILE;
      const tileset = packed === EMPTY_TILE ? undefined : tilesets[tilesetIndexOf(packed)];
      if (tileset !== undefined) {
        out.tileset = tileset.id;
        out.tile = tileIdOf(packed);
        anyAbove = true;
      }
    }
  }
}

/**
 * Whether a rule lets a prop land where a stack is painted: `only` where any of its kinds is in the stack (a terrain
 * kind painted in any terrain layer, a tileset kind the top-most layer's with any tile, a tile kind that tile alone,
 * a zone kind the point inside), `never`
 * where none is, so the two are complements over every point.
 *
 * @param rule The rule, if any; none lands anywhere.
 * @param stack What `paintAt` found at the foot point.
 * @param inZone Whether the foot point is inside a zone kind; absent, it is inside none.
 * @returns True when the prop may land there.
 */
export function paintAllows(rule: GroundRule | undefined, stack: PaintStack, inZone?: ZoneTest): boolean {
  if (rule === undefined) {
    return true;
  }
  let listed = false;
  for (const kind of rule.kinds) {
    if ("zone" in kind) {
      listed = inZone?.(kind) === true;
    } else if ("tileset" in kind) {
      listed = stack.tileset === kind.tileset && (kind.tile === undefined || stack.tile === kind.tile);
    } else {
      for (let i = 0; i < stack.count; i += 1) {
        if (stack.terrains[i] === kind.terrain && stack.colours[i] === kind.colour) {
          listed = true;
          break;
        }
      }
    }
    if (listed) {
      break;
    }
  }
  return rule.mode === "only" ? listed : !listed;
}

/**
 * Whether a point is inside a zone kind: the kind is of the open world, the map holds a zone layer of its id, and
 * that layer's collision cell where the feet stand (the one holding the pixel row above the foot line) is painted.
 * A zone is an area, not paint: any number of zone layers may hold a point, and which wins for play is not read.
 *
 * @param map The map.
 * @param world The open world's id.
 * @param kind The zone kind.
 * @param tileSize Tile size in pixels.
 * @param footX The foot point's x in whole pixels.
 * @param footY The foot point's y in whole pixels.
 * @returns True when the point is inside.
 */
export function insideZone(map: MapDocument, world: string, kind: ZoneKind, tileSize: number, footX: number, footY: number): boolean {
  if (kind.world !== world) {
    return false;
  }
  const layer = map.layers.find((l) => l.id === kind.zone);
  if (layer?.kind !== "zone") {
    return false;
  }
  const across = map.width * COLLISION_CELLS_PER_TILE;
  const cell = tileSize / COLLISION_CELLS_PER_TILE;
  const cx = Math.floor(footX / cell);
  const cy = Math.floor((footY - 1) / cell);
  return cx >= 0 && cy >= 0 && cx < across && cy < map.height * COLLISION_CELLS_PER_TILE && (layer.cells[cy * across + cx] ?? 0) !== 0;
}

/**
 * Whether a rule's kind names a ground: the same terrain and colour, the same tileset when the kind names no tile,
 * the same tileset and tile when it names one, or the same zone.
 *
 * @param a The rule's kind.
 * @param b The ground.
 * @returns True when they match.
 */
function sameGround(a: GroundKind, b: GroundKind): boolean {
  if ("tileset" in a) {
    return "tileset" in b && a.tileset === b.tileset && (a.tile === undefined || a.tile === b.tile);
  }
  if ("terrain" in a) {
    return "terrain" in b && a.terrain === b.terrain && a.colour === b.colour;
  }
  return "zone" in b && a.zone === b.zone && a.world === b.world;
}

/**
 * Whether one point can hold two kinds at once: the same kind; a zone and any paint; two zones of one world, which may
 * overlap; two terrain entries, which lie in different layers (`paintAt`); or a terrain and a tileset, a tile layer
 * lying over a terrain one; a tile and its tileset, or the same tile. Two different tilesets cannot, nor two
 * different tiles of one tileset, and zones of two worlds are never under one dab.
 *
 * @param a One kind.
 * @param b The other.
 * @returns True when some point may hold both.
 */
function canShareAPoint(a: GroundKind, b: GroundKind): boolean {
  if ("zone" in a && "zone" in b) {
    return a.world === b.world;
  }
  if ("zone" in a || "zone" in b) {
    return true;
  }
  if ("terrain" in a || "terrain" in b) {
    return true;
  }
  return a.tileset === b.tileset && (a.tile === undefined || b.tile === undefined || a.tile === b.tile);
}

/**
 * Whether a ground rule lets a prop land at a point: no rule lands anywhere, `only` where the paint is one of its
 * paint kinds or the point is inside one of its zone kinds (so never where nothing is painted and no zone holds the
 * point), `never` where neither holds.
 *
 * @param rule The rule, if any.
 * @param ground The paint at the foot point (`groundAt`).
 * @param inZone Whether the foot point is inside a zone kind; absent, it is inside none.
 * @returns True when the prop may land there.
 */
export function groundAllows(rule: GroundRule | undefined, ground: GroundKind | null, inZone?: ZoneTest): boolean {
  if (rule === undefined) {
    return true;
  }
  const listed = rule.kinds.some((kind) => ("zone" in kind ? inZone?.(kind) === true : ground !== null && sameGround(kind, ground)));
  return rule.mode === "only" ? listed : !listed;
}

/**
 * The grounds a point holding a kind may read as, for `groundRulesMeet`: a zone kind a point with no paint, a tile
 * kind or a terrain kind that paint, and a tileset kind with no tile of its own each tile of the tileset either rule
 * names and a tile neither names (`OTHER_TILE`), since a tileset has more tiles than a rule lists.
 *
 * @param kind The kind a point holds.
 * @param set The set's rule.
 * @param entry The entry's rule.
 * @returns The paints to try; a null paint is unpainted ground.
 */
function pointsHolding(kind: GroundKind, set: GroundRule, entry: GroundRule): (GroundKind | null)[] {
  if ("zone" in kind) {
    return [null];
  }
  if (!("tileset" in kind) || kind.tile !== undefined) {
    return [kind];
  }
  const points: GroundKind[] = [{ tileset: kind.tileset, tile: OTHER_TILE }];
  for (const rule of [set, entry]) {
    for (const named of rule.kinds) {
      if ("tileset" in named && named.tileset === kind.tileset && named.tile !== undefined) {
        points.push(named);
      }
    }
  }
  return points;
}

/**
 * Whether any ground passes both a set's rule and an entry's (ADR-123's notes of 2026-10-09, P1.6-T191 and
 * P1.6-T192): false only when the set's rule leaves the entry nowhere to stand on any map, as a set never on road
 * over an entry only on road. Two `never` rules always meet on unpainted ground outside every zone; an absent rule
 * meets any rule, which names at least one kind. Two `only` rules meet when a kind of one and a kind of the other
 * can hold one point (`canShareAPoint`): a point may be inside a zone and on paint, or inside two zones of one
 * world, at once. Otherwise each kind of the
 * `only` rule is tried at a point holding that kind alone.
 *
 * @param set The set's rule, if any.
 * @param entry The entry's rule, if any.
 * @returns True when some ground, unpainted ground included, passes both.
 */
export function groundRulesMeet(set: GroundRule | undefined, entry: GroundRule | undefined): boolean {
  if (set === undefined || entry === undefined) {
    return true;
  }
  if (set.mode === "never" && entry.mode === "never") {
    return true;
  }
  if (set.mode === "only" && entry.mode === "only") {
    return set.kinds.some((a) => entry.kinds.some((b) => canShareAPoint(a, b)));
  }
  const candidates = set.mode === "only" ? set.kinds : entry.kinds;
  return candidates.some((kind) =>
    pointsHolding(kind, set, entry).some((paint) => {
      const inZone: ZoneTest = (zone) => "zone" in kind && sameGround(zone, kind);
      return groundAllows(set, paint, inZone) && groundAllows(entry, paint, inZone);
    }),
  );
}

/**
 * The ground a pressed pixel names, for the brush set editor's eyedropper (ADR-123's amendment of 2026-10-13, A5.4):
 * what `groundAt` answers at the pixel's foot point, one pixel below the press, so the kind picked is the one a prop
 * stood there would be read on. A tile is `{ tileset, tile }`.
 *
 * @param map The map.
 * @param tilesets The world's tilesets.
 * @param tileSize Tile size in pixels.
 * @param pixelX The pressed pixel's x in whole map pixels.
 * @param pixelY The pressed pixel's y in whole map pixels.
 * @returns The paint there, or null where nothing is painted.
 */
export function groundUnderPixel(map: MapDocument, tilesets: readonly TilesetRef[], tileSize: number, pixelX: number, pixelY: number): GroundKind | null {
  return groundAt(map, tilesets, tileSize, pixelX, pixelY + 1);
}
